import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  Maximize,
  Minimize,
  BookOpen,
  Loader2,
  AlertCircle,
  PanelLeftClose,
  PanelLeft
} from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { fetchDocumentData } from "../utils/documentStore";

// Configure local worker to prevent CDN failures and CORS issues
if (typeof window !== "undefined" && pdfjs?.GlobalWorkerOptions) {
  const version = pdfjs.version || "5.4.296";
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
}

/** Compute a responsive default zoom scale based on viewport width */
const getDefaultScale = () => {
  if (typeof window === "undefined") return 1.0;
  const w = window.innerWidth;
  if (w < 480) return 0.6;   // phones
  if (w < 768) return 0.85;  // small tablets
  if (w < 1024) return 1.0;  // tablets
  return 1.3;                // desktop
};

/** Check if the current viewport is mobile-sized */
const isMobileViewport = () => typeof window !== "undefined" && window.innerWidth < 768;

/**
 * PdfViewerModal
 * 
 * Full-screen in-platform PDF viewer in clean Light Mode.
 * Features: page navigation, thumbnail sidebar, zoom controls, download, fullscreen.
 *
 * Props:
 *   url         - presigned B2 URL or blob URL
 *   mediaId     - CaseMedia ID for backend streaming
 *   filename    - display name
 *   cachedData  - pre-loaded document data { arrayBuffer, blobUrl } from InlineDocumentViewer
 *   initialPage - initial page number to display
 *   onClose     - close handler
 */
const PdfViewerModal = ({ url, mediaId, filename, cachedData, initialPage = 1, onClose }) => {
  const [pdfFile, setPdfFile] = useState(cachedData?.blobUrl || url || null);
  const [downloadBlobUrl, setDownloadBlobUrl] = useState(cachedData?.blobUrl || null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(initialPage || 1);
  const [scale, setScale] = useState(getDefaultScale);
  const [inputPage, setInputPage] = useState(String(initialPage || 1));
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());
  const [loadError, setLoadError] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(!cachedData?.arrayBuffer);

  const containerRef = useRef(null);
  const mainScrollRef = useRef(null);
  const dpr = typeof window !== "undefined" && window.devicePixelRatio ? Math.min(2, window.devicePixelRatio) : 1;

  // Reset scroll position to top whenever page number changes
  useEffect(() => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
  }, [pageNumber]);

  const pdfOptions = useMemo(() => ({
    disableRange: true,
    disableStream: true,
    disableAutoFetch: true,
  }), []);

  // If no cachedData was provided, fetch document bytes via documentStore
  useEffect(() => {
    if (cachedData?.blobUrl) {
      setPdfFile(cachedData.blobUrl);
      setDownloadBlobUrl(cachedData.blobUrl);
      setLoadingDoc(false);
      return;
    }

    let cancelled = false;
    setLoadingDoc(true);
    setLoadError(false);

    const load = async () => {
      try {
        const result = await fetchDocumentData({
          mediaId,
          url,
          mimeType: "application/pdf",
        });
        if (cancelled) return;
        setPdfFile(result.blobUrl || url);
        setDownloadBlobUrl(result.blobUrl);
      } catch (err) {
        if (!cancelled) {
          console.error("PdfViewerModal load error:", err);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoadingDoc(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [cachedData, mediaId, url]);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setLoadError(false);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    console.error("PDF Load Error:", err);
    setLoadError(true);
  }, []);

  const goTo = (n) => {
    const clamped = Math.max(1, Math.min(n, numPages || 1));
    setPageNumber(clamped);
    setInputPage(String(clamped));
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") goTo(pageNumber - 1);
    if (e.key === "ArrowRight") goTo(pageNumber + 1);
    if (e.key === "Escape") onClose();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Sync fullscreen state when exiting via Escape key or browser chrome
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  // ── Touch Swipe Navigation ──
  const touchStartRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;

    // Must be predominantly horizontal swipe, at least 50px, within 500ms
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 500) {
      if (dx < 0) {
        // Swipe left → next page
        goTo(pageNumber + 1);
      } else {
        // Swipe right → previous page
        goTo(pageNumber - 1);
      }
    }
  }, [pageNumber, numPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const finalDownloadUrl = downloadBlobUrl || url;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] flex flex-col bg-[#F5F7FA] select-none transition-colors duration-200"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* ── Header ── */}
      <header className="flex items-center justify-between px-3 sm:px-5 md:px-6 py-2.5 sm:py-3 bg-white border-b border-gray-200 text-gray-800 shrink-0 shadow-xs">
        {/* Title + Sidebar Toggle */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
          {/* Sidebar toggle button */}
          <button
            type="button"
            onClick={() => setSidebarOpen((prev) => !prev)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center"
            title={sidebarOpen ? "Hide Thumbnails" : "Show Thumbnails"}
          >
            {sidebarOpen ? <PanelLeftClose size={17} /> : <PanelLeft size={17} />}
          </button>
          <BookOpen size={18} className="text-indigo-600 shrink-0 hidden sm:block" />
          <h2 className="text-sm font-bold text-gray-900 truncate max-w-[120px] sm:max-w-[180px] md:max-w-md" title={filename}>
            {filename}
          </h2>
        </div>

        {/* Zoom & Page Controls */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
          {numPages && (
            <span className="text-gray-500 text-xs font-medium hidden md:inline-flex items-center gap-1">
              Page{" "}
              <input
                type="number"
                value={inputPage}
                min={1}
                max={numPages}
                onChange={(e) => setInputPage(e.target.value)}
                onBlur={() => goTo(parseInt(inputPage, 10) || 1)}
                onKeyDown={(e) => e.key === "Enter" && goTo(parseInt(inputPage, 10) || 1)}
                className="w-10 text-center bg-gray-50 text-gray-800 rounded-md px-1 py-0.5 text-xs border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-semibold"
              />{" "}
              of {numPages}
            </span>
          )}

          {/* Zoom Pill — hidden on small mobile, visible from sm up */}
          <div className="hidden sm:flex items-center bg-gray-100 border border-gray-200 rounded-xl p-0.5">
            <button
              onClick={() => setScale((s) => Math.max(0.3, s - 0.2))}
              title="Zoom out"
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              <ZoomOut size={15} />
            </button>
            <span className="text-xs font-semibold text-gray-700 font-mono w-11 text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(3, s + 0.2))}
              title="Zoom in"
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              <ZoomIn size={15} />
            </button>
            <button
              onClick={() => setScale(getDefaultScale())}
              title="Reset Zoom"
              className="px-2 py-1 text-[11px] font-semibold rounded-lg text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
            >
              Reset
            </button>
          </div>

          {/* Mobile-only compact zoom buttons */}
          <div className="flex sm:hidden items-center gap-0.5">
            <button
              onClick={() => setScale((s) => Math.max(0.3, s - 0.15))}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all"
              title="Zoom out"
            >
              <ZoomOut size={14} />
            </button>
            <span className="text-[10px] font-semibold text-gray-600 font-mono w-8 text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(3, s + 0.15))}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all"
              title="Zoom in"
            >
              <ZoomIn size={14} />
            </button>
          </div>
        </div>

        {/* Right Action Cluster */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <a
            href={finalDownloadUrl}
            download={filename}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center"
            title="Download Document"
          >
            <Download size={16} />
          </a>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center"
            title={isFullscreen ? "Exit Fullscreen" : "Toggle Fullscreen"}
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors ml-1 flex items-center justify-center"
            title="Close Viewer"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>
      </header>

      {/* ── Body: Single Shared Document enclosing both Thumbnail Sidebar and Main Canvas ── */}
      {loadingDoc ? (
        <div className="flex-1 flex flex-col items-center justify-center p-16 text-gray-500">
          <Loader2 size={36} className="animate-spin text-indigo-600 mb-3" />
          <span className="text-sm font-medium">Loading document…</span>
        </div>
      ) : loadError ? (
        <div className="flex-1 flex flex-col items-center justify-center p-12 text-red-600 gap-3 max-w-md text-center mx-auto my-auto bg-white rounded-2xl border border-red-200 shadow-sm">
          <AlertCircle size={40} className="text-red-500" />
          <p className="text-sm font-semibold">Failed to load PDF preview.</p>
          <a
            href={finalDownloadUrl}
            download={filename}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Download File Instead
          </a>
        </div>
      ) : pdfFile ? (
        <Document
          file={pdfFile}
          options={pdfOptions}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex-1 flex flex-col items-center justify-center p-16 text-gray-500">
              <Loader2 size={36} className="animate-spin text-indigo-600 mb-3" />
              <span className="text-sm font-medium">Parsing PDF pages…</span>
            </div>
          }
          className="flex flex-1 overflow-hidden"
        >
          {/* Thumbnail Sidebar: Collapsible — hidden on mobile by default, toggled via header button */}
          {numPages && (
            <div
              className={`bg-white border-r border-gray-200 overflow-y-auto py-3 px-2 space-y-2 shrink-0 scrollbar-hide transition-all duration-250 ease-in-out ${
                sidebarOpen
                  ? "w-24 sm:w-28 opacity-100"
                  : "w-0 opacity-0 px-0 overflow-hidden border-r-0"
              }`}
            >
              {sidebarOpen && Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  type="button"
                  onClick={() => goTo(pg)}
                  className={`w-full rounded-lg overflow-hidden border-2 transition-all p-0.5 bg-white ${
                    pg === pageNumber ? "border-indigo-600 shadow-sm" : "border-gray-200 hover:border-gray-400"
                  }`}
                  title={`Page ${pg}`}
                >
                  <div className="w-full flex justify-center bg-gray-50 overflow-hidden">
                    <Page
                      pageNumber={pg}
                      width={80}
                      devicePixelRatio={1}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </div>
                  <p className={`text-center text-[10px] py-0.5 font-bold ${pg === pageNumber ? "text-indigo-600" : "text-gray-400"}`}>
                    {pg}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Main Page Viewer */}
          <div
            ref={mainScrollRef}
            className="flex-1 overflow-auto py-4 px-3 sm:py-8 sm:px-6 bg-[#F5F7FA]"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-fit mx-auto shadow-lg border border-gray-300 rounded-sm bg-white">
              <Page
                pageNumber={pageNumber}
                scale={scale}
                devicePixelRatio={dpr}
                renderAnnotationLayer={true}
                renderTextLayer={true}
              />
            </div>
          </div>
        </Document>
      ) : null}

      {/* ── Footer navigation ── */}
      <footer className="flex items-center justify-between px-3 sm:px-8 py-2 sm:py-3 bg-white border-t border-gray-200 shrink-0 text-xs text-gray-600 shadow-xs">
        <div className="flex items-center gap-2 font-bold text-gray-900">
          <span className="hidden sm:inline">Page</span> {pageNumber}<span className="text-gray-400 font-medium">/</span>{numPages || 1}
        </div>

        {/* Progress Bar — visible on all screens */}
        {numPages && (
          <div className="flex-1 max-w-[100px] sm:max-w-xs mx-3 sm:mx-6 flex items-center gap-2">
            <div className="flex-1 h-1 sm:h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                style={{ width: `${(pageNumber / numPages) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => goTo(pageNumber - 1)}
            disabled={pageNumber <= 1}
            className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs flex items-center gap-0.5 sm:gap-1"
          >
            <ChevronLeft size={14} /> <span className="hidden sm:inline">Prev</span>
          </button>
          <button
            type="button"
            onClick={() => goTo(pageNumber + 1)}
            disabled={!numPages || pageNumber >= numPages}
            className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs flex items-center gap-0.5 sm:gap-1"
          >
            <span className="hidden sm:inline">Next</span> <ChevronRight size={14} />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default PdfViewerModal;
